/**
 * Country code picker (default +91) + number-only box, for the WhatsApp sign-ins (owner 2026-10-04:
 * typing "+" on a phone keypad is awkward). Controlled by the full number the server expects
 * ("+919876543210"); a pasted full number selects its code (lib/phoneEntry.ts). The picker is the
 * native <select> — the OS wheel / list, never a web popup.
 */
import { useState } from "react";
import { COUNTRY_CODES, DEFAULT_CODE, fullPhone, splitPhoneInput } from "@/lib/phoneEntry";

interface Props {
  id: string;
  /** The full number ("+919876543210") or "". */
  value: string;
  onChange: (full: string) => void;
  disabled?: boolean;
  className?: string;
}

export default function PhoneField({ id, value, onChange, disabled, className }: Props) {
  const initial = value ? splitPhoneInput(value, DEFAULT_CODE) : { code: DEFAULT_CODE, local: "" };
  const [code, setCode] = useState(initial.code);
  const [local, setLocal] = useState(initial.local);

  const update = (nextCode: string, nextLocal: string) => {
    setCode(nextCode);
    setLocal(nextLocal);
    onChange(fullPhone(nextCode, nextLocal));
  };

  const box = "h-11 rounded-md border border-gray-600 bg-black text-white focus:border-yellow-600 focus:outline-none disabled:opacity-60";
  return (
    <div className={`flex gap-2 ${className ?? ""}`}>
      {/* The visible chip shows only the code ("+91 ▾"); the native <select> lies invisibly on top
          of it, so a tap opens the OS picker with full country names. */}
      <label className={`${box} relative shrink-0 inline-flex items-center gap-1 px-3 text-base`} style={{ width: 92 }}>
        <span aria-hidden>{code || "+…"}</span>
        <span aria-hidden className="text-gray-400" style={{ fontSize: 11 }}>▼</span>
        <select
          aria-label="Country code"
          value={code}
          disabled={disabled}
          onChange={(e) => update(e.target.value, local)}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        >
          {COUNTRY_CODES.map((c) => (
            <option key={c.code} value={c.code}>{c.label}</option>
          ))}
          <option value="">Other (type +code)</option>
        </select>
      </label>
      <input
        id={id}
        type="tel"
        inputMode={code ? "numeric" : "tel"}
        autoComplete="tel-national"
        placeholder={code === "+91" ? "98xxx xxxxx" : code ? "Mobile number" : "+code number"}
        value={local}
        disabled={disabled}
        onChange={(e) => { const s = splitPhoneInput(e.target.value, code); update(s.code, s.local); }}
        className={`${box} min-w-0 flex-1 px-3 text-base placeholder-gray-500`}
      />
    </div>
  );
}
